import { useSelector } from "react-redux";

export const isAllDataFilledStage3 = (testMethodsObj) => {
  // Check if the object is null or undefined
  if (testMethodsObj === null || testMethodsObj === undefined) {
    return false;
  }

  // Get all test method keys (UT, MT, etc.)
  const testMethodKeys = Object.keys(testMethodsObj);

  // If no test methods, return false
  if (testMethodKeys.length === 0) {
    return false;
  }

  // Check each test method
  for (const methodKey of testMethodKeys) {
    const method = testMethodsObj[methodKey];

    // Check for title (required field)
    if (!method.title || method.title.trim() === "") {
      return false;
    }

    // Check for images (must be an array and not empty)
    if (!Array.isArray(method.images) || method.images.length === 0) {
      return false;
    }

    // Check each image has required fields
    for (const image of method.images) {
      if (!image.url || !image.preparedBy || !image.approvedBy) {
        return false;
      }
    }

    // Check rows if they exist
    if (method.rows && Array.isArray(method.rows)) {
      for (const row of method.rows) {
        // Check only the specific fields in each row
        const fieldsToCheck = [
          "thickness_1",
          "thickness_2",
          "length",
          "material",
          "astm_qt",
          "ut_mt",
        ];
        for (const field of fieldsToCheck) {
          if (
            row[field] === "" ||
            row[field] === null ||
            row[field] === undefined
          ) {
            return false;
          }
        }
      }
    } else {
      return false;
    }
  }

  return true;
};

export const isAllDataFilledStage4 = (testMethodsObj) => {
  // Check if the object is null or undefined
  if (testMethodsObj === null || testMethodsObj === undefined) {
    return false;
  }

  // Get all test method keys (UT, MT, etc.)
  const testMethodKeys = Object.keys(testMethodsObj);

  // If no test methods, return false
  if (testMethodKeys.length === 0) {
    return false;
  }

  // Check each test method
  for (const methodKey of testMethodKeys) {
    const method = testMethodsObj[methodKey];

    // Check for title (required field)
    if (!method.title || method.title.trim() === "") {
      return false;
    }

    // Check for images (must be an array and not empty)
    if (!Array.isArray(method.documents) || method.documents.length === 0) {
      return false;
    }

    // Check each image has required fields
    for (const image of method.documents) {
      if (!image.url || !image.preparedBy || !image.approvedBy) {
        return false;
      }
    }

    // Check rows if they exist
    if (method.rows && Array.isArray(method.rows)) {
      for (const row of method.rows) {
        // Check only the specific fields in each row
        const fieldsToCheck = ["description"];
        for (const field of fieldsToCheck) {
          if (
            row[field] === "" ||
            row[field] === null ||
            row[field] === undefined
          ) {
            return false;
          }
        }
      }
    } else {
      return false;
    }
  }

  return true;
};

export const submitableDataForStep3 = (title, localData, temporaryRFIId) => {
  return {
    title,
    uid: temporaryRFIId,
    rows: localData.map((row) => ({
      joint_number:
        row.joint_number?.toString() || (localData.indexOf(row) + 1).toString(), // Ensure joint_number is included
      thickness_1: Number(row.thickness_1) || 0,
      thickness_2: Number(row.thickness_2) || 0,
      length: Number(row.length) || 0,
      material: Number(row.material) || 0,
      astm_qt: row.astm_qt || "",
      ut_mt: row.ut_mt || "",
    })),
  };
};

export const renameSubpartsKey = (obj) => {
  // Create a deep copy of the object to avoid modifying the original
  const newObj = JSON.parse(JSON.stringify(obj));

  // Iterate through each component in the data array
  if (newObj.data && Array.isArray(newObj.data)) {
    newObj.data.forEach((component) => {
      if (component.items && Array.isArray(component.items)) {
        component.items.forEach((item) => {
          // Check if subparts exists and rename it to sub_parts
          if ("subparts" in item) {
            item.sub_parts = item.subparts;
            delete item.subparts;
          }
        });
      }
    });
  }

  return newObj;
};

export const transformObject = (obj) => {
  // Create a deep copy of the object to avoid modifying the original
  const transformed = JSON.parse(JSON.stringify(obj));

  // Rename template_components to components if it exists
  if ("template_components" in transformed) {
    transformed.components = transformed.template_components;
    delete transformed.template_components;

    // Process each component to rename subparts to sub_parts
    transformed.components.forEach((component) => {
      if (component.items) {
        component.items.forEach((item) => {
          if (item.subparts) {
            item.sub_parts = item.subparts;
            delete item.subparts;
          }
        });
      }
    });
  }

  return transformed;
};

export const transformSubpartsKey = (obj) => {
  // Make a deep copy of the object to avoid modifying the original
  const transformed = JSON.parse(JSON.stringify(obj));

  if (transformed.items && Array.isArray(transformed.items)) {
    transformed.items.forEach((item) => {
      if (item.sub_parts !== undefined) {
        // Rename sub_parts to subparts
        item.subparts = item.sub_parts;
        delete item.sub_parts;
      }
    });
  }

  return transformed;
};

export const formatText = (input) => {
  if (!input) return input;

  const words = input.split("_");

  const formattedWords = words.map((word) => {
    if (word.length === 0) return "";
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return formattedWords.join(" ");
};

/**
 * Format a timestamp into 'DD/MM/YYYY HH:mm'. Returns empty string on invalid.
 * Accepts common backend formats like 'YYYY-MM-DD HH:mm:ss' and normalizes missing timezone to UTC.
 * Pass { dateOnly: true } as the second argument to return only 'DD/MM/YYYY'.
 */
export function formatDateTimeDMY(input, options = {}) {
  if (!input) return "";
  let s = input;
  try {
    if (typeof input === "string") {
      s = input.trim();
      // Convert "YYYY-MM-DD HH:mm:ss" => "YYYY-MM-DDTHH:mm:ss"
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s) && !s.includes("T")) {
        s = s.replace(" ", "T");
      }
      // If there's no timezone, append 'Z' to treat as UTC
      if (
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) &&
        !/(?:[zZ]|[-+]\d{2}:?\d{2})$/.test(s)
      ) {
        s = `${s}Z`;
      }
    }

    const d =
      typeof s === "number" ? new Date(s < 1e12 ? s * 1000 : s) : new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const datePart = `${dd}/${mm}/${yyyy}`;
    if (options?.dateOnly) return datePart;
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} ${hh}:${min}`;
  } catch (_) {
    return "";
  }
}

/**
 * Cross-browser friendly relative time formatter.
 * Normalizes common backend timestamp strings for Safari compatibility.
 */
export function formatRelativeTime(input) {
  if (!input) return "";

  let ts;
  if (typeof input === "string") {
    let s = input.trim();
    // Convert "YYYY-MM-DD HH:mm:ss" => "YYYY-MM-DDTHH:mm:ss"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s) && !s.includes("T")) {
      s = s.replace(" ", "T");
    }
    // If there's no timezone, append 'Z' to treat as UTC
    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) &&
      !/(?:[zZ]|[-+]\d{2}:?\d{2})$/.test(s)
    ) {
      s = `${s}Z`;
    }
    ts = new Date(s);
  } else if (typeof input === "number") {
    // Assume milliseconds. If seconds (10-digit), convert to ms.
    ts = new Date(input < 1e12 ? input * 1000 : input);
  } else {
    ts = input;
  }

  if (Number.isNaN(ts?.getTime?.())) return "";

  const now = new Date();
  const diffMs = now - ts;
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const week = Math.floor(day / 7);

  if (sec < 45) return "just now";
  if (min < 60) return `${min} min${min !== 1 ? "s" : ""} ago`;
  if (hr < 24) return `${hr} hour${hr !== 1 ? "s" : ""} ago`;
  if (day < 7) return `${day} day${day !== 1 ? "s" : ""} ago`;
  return `${week} week${week !== 1 ? "s" : ""} ago`;
}

/**
 * Returns relative time where possible, otherwise a locale string fallback.
 */
export function formatDisplayTime(input) {
  const rel = formatRelativeTime(input);
  if (rel) return rel;
  try {
    const d = new Date(input);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString();
    }
  } catch (_) {
    /* noop */
  }
  return String(input ?? "");
}

export function addBaseUrlToImages(components, baseUrl) {
  // Make sure baseUrl ends with a slash for proper concatenation
  if (baseUrl && !baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  // Iterate through each component
  return components.map((component) => {
    // Check if component has images
    if (component.component_images && component.component_images.length > 0) {
      // Add baseUrl to each image_url
      const updatedImages = component.component_images.map((image) => {
        // Only add baseUrl if it's not already present
        if (image.image_url && !image.image_url.startsWith(baseUrl)) {
          return {
            ...image,
            image_url: baseUrl + image.image_url.replace(/^\//, ""), // Remove leading slash if present
          };
        }
        return image;
      });

      return {
        ...component,
        component_images: updatedImages,
      };
    }
    return component;
  });
}

export function convertToValueLabelFormat(dataArray) {
  return dataArray.map((item) => ({
    value: String(item),
    label: String(item),
  }));
}

export function getTimeSincePastDate(pastDate, currentDate = new Date()) {
  // Parse the past date (handle both Date objects and strings)
  const past = typeof pastDate === "string" ? new Date(pastDate) : pastDate;

  // Format current date to match pastDate format if pastDate is a string
  let now;
  if (typeof pastDate === "string") {
    // If pastDate is a string, format currentDate to match the same string format
    now = new Date(currentDate);

    // If the pastDate string includes time information, ensure now has the same precision
    const pastDateHasTime =
      pastDate.includes("T") ||
      pastDate.includes(" ") ||
      /[0-9]:[0-9]/.test(pastDate);

    if (!pastDateHasTime) {
      // If pastDate doesn't have time, set current date to start of day for consistent comparison
      now.setHours(0, 0, 0, 0);
    }
  } else {
    now = currentDate;
  }

  // Validate both dates
  if (isNaN(past.getTime()) || isNaN(now.getTime())) {
    throw new Error("Invalid date provided");
  }

  // Calculate difference in milliseconds
  const diffMs = now - past;

  // If date is in the future, return all zeros
  if (diffMs < 0) {
    return {
      days: 0,
      hours: 0,
      totalHours: 0,
      totalDays: 0,
    };
  }

  // Constants for time calculations
  const msPerDay = 86400000; // 1000 * 60 * 60 * 24
  const msPerHour = 3600000; // 1000 * 60 * 60

  // Calculate days and remaining hours
  const days = Math.floor(diffMs / msPerDay);
  const remainingMs = diffMs % msPerDay;
  const hours = Math.floor(remainingMs / msPerHour);

  return {
    days: days,
    hours: hours,
    totalHours: Math.floor(diffMs / msPerHour),
    totalDays: diffMs / msPerDay,
  };
}

export function addBaseUrlToDocuments(data, baseUrl) {
  // Check if data has ndt_documents array
  if (!data || !data.ndt_documents || !Array.isArray(data.ndt_documents)) {
    throw new Error("Invalid data format: ndt_documents array is required");
  }

  // Ensure baseUrl ends with a slash if it doesn't already
  let formattedBaseUrl = baseUrl;
  if (formattedBaseUrl && !formattedBaseUrl.endsWith("/")) {
    formattedBaseUrl += "/";
  }

  // Process each document in the array
  const updatedDocuments = data.ndt_documents.map((document) => {
    // Remove leading slash from document path if present to avoid double slashes
    let documentPath = document.document;
    if (documentPath.startsWith("/")) {
      documentPath = documentPath.substring(1);
    }

    return {
      ...document,
      document: formattedBaseUrl + documentPath,
    };
  });

  return {
    ...data,
    ndt_documents: updatedDocuments,
  };
}

export const isStageInViewMode = ({ stageNumber, maxAllowedStage }) => {
  return stageNumber !== maxAllowedStage;
};

export const scrollToError = () => {
  // Small delay to ensure DOM is updated after form submission
  setTimeout(() => {
    // Find all error elements
    const errorElements = document.querySelectorAll(
      '.Mui-error, [aria-invalid="true"], .error-field, [data-error="true"]'
    );

    if (errorElements.length > 0) {
      // Scroll to the first error element
      errorElements[0].scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // Focus on the first input in the error element
      const inputElement = errorElements[0].querySelector(
        "input, textarea, select"
      );
      if (inputElement && inputElement.focus) {
        inputElement.focus();
      }
    } else {
      // Fallback: try to find elements by error class or data attributes
      const errorField = document.querySelector(
        '[class*="error"], [data-error], [aria-invalid]'
      );
      if (errorField) {
        errorField.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }
  }, 100);
};

export const hasPermission = (permissions, action) => {
  return permissions?.includes(action);
};
